import {Component, inject, OnInit} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {DatePipe} from "@angular/common";
import { RelationshipEnum } from "../../../../../../core/models/relationship.enum";
import { CommunityService } from "../../../../../../core/services/connected/community.service";
import { AuthenticationService } from "../../../../../../core/services/authentication.service";
import { ApiRoutesService } from "../../../../../../core/services/api-routes.service";

@Component({
  selector: 'admin-onboarding-profile',
  templateUrl: './admin-onboarding-profile.page.html',
  styleUrls: ['./admin-onboarding-profile.page.scss']
})
export class AdminOnboardingProfilePage implements OnInit {

  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #communityService = inject(CommunityService);
  readonly #authentService = inject(AuthenticationService);
  readonly #apiRoutes = inject(ApiRoutesService);
  readonly #datePipe = inject(DatePipe);

  private _profileForm: FormGroup | null = null;
  private _relationshipKeys = Object.keys(RelationshipEnum);
  private _selectedFile: File | null = null;
  private _imagePreview: string | ArrayBuffer | null = null;

  readonly #communityId: string = this.#activatedRoute.snapshot.params['communityId'];

  ngOnInit(): void {
    const routeState = window.history.state;

    this._profileForm = new FormGroup({
      lastName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      firstName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      nickName: new FormControl(''),
      birthDate: new FormControl(''),
      phone: new FormControl('', [Validators.required, Validators.pattern(/^0[1-9](?:[ .-]?[0-9]{2}){4}$/)]),
      relationship: new FormControl('', [Validators.required]),
      creatorEmail: new FormControl('', [Validators.required, Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')]),
      creatorLastName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      creatorFirstName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    });

    if (routeState && routeState['profile']) {
      this._profileForm?.patchValue({
        lastName: routeState['profile'].firstName,
        firstName: routeState['profile'].lastName,
        nickName: routeState['profile'].nickName,
        birthDate: routeState['profile'].birthDate,
        phone: routeState['profile'].phone,
        relationship: routeState['profile'].relationship,
      })
      this.selectedFile = routeState['profilePicture'];
    }

    if (this.#communityId) {
      this.#communityService.getCommunityById(this.#communityId).subscribe((community) => {
        this._profileForm?.patchValue({
          lastName: community.relative.lastname,
          firstName: community.relative.firstname,
          nickName: community.relative.nickname,
          birthDate: this.#datePipe.transform(community.relative.birthDate, 'yyyy-MM-dd'),
          phone: community.relative.phone,
        })
        if (community && community.relative.hasPicture) {
          this._imagePreview = `${this.#apiRoutes.community()}/${community.id}/picture?access_token=${this.#authentService.accessToken}&t=${new Date().getTime()}`;
        } else {
          this._imagePreview = '/assets/images/default-profile-picture.png';
        }
      })
    }
  }

  get profileForm(): FormGroup | null {
    return this._profileForm;
  }

  get communityId(): string {
    return this.#communityId;
  }

  get selectedFile(): File | null {
    return this._selectedFile;
  }

  set selectedFile(value: File) {
    this._selectedFile = value;

    const reader = new FileReader();
    reader.onload = (e) => {
      this._imagePreview = reader.result;
    };
    reader.readAsDataURL(value);
  }

  get imagePreview(): string | ArrayBuffer | null {
    return this._imagePreview;
  }

  getRelationshipValues() {
    return this._relationshipKeys.map(key => RelationshipEnum[key as keyof typeof RelationshipEnum]);
  }

  onFileSelected(selectedFileEvent: any) {
    const file = selectedFileEvent.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  onRemove() {
    this._selectedFile = null;
    this._imagePreview = null;
  }

  createProfile() {
    if (this._profileForm?.valid) {
      this._profileForm.value.birthDate = new Date(this._profileForm.value.birthDate).getTime();

      if (this.communityId) {
        this.#router.navigate(["/admin/onboarding/" + this.communityId + "/address"], {
          state: {
            profile: this._profileForm?.value,
            profilePicture: this._selectedFile
          }
        });
      } else {
        this.#router.navigate(["/admin/onboarding/address"], {
          state: {
            profile: this._profileForm?.value,
            profilePicture: this._selectedFile
          }
        });
      }
    }
  }
}
